/* eslint-disable max-len */
/* eslint-disable react-hooks/exhaustive-deps */
import _ from 'lodash';
import { useEffect, useRef } from 'react';
import JsonQuery from 'json-query';
import { hooks as commonHooks } from '@sitebuilder/common';
import { isLocalStorageAccessible } from '../utils';
import { ColumnOption, SelectionListData } from './CustomizedFundList';

interface PropsShape {
    columns: ColumnOption[];
    data: any;
    widgetInstance: any;
    enableWatchlist: boolean;
    watchlist: string[];
    setWatchlist: (checklist: string[]) => void;
    watchlistStorageName?: string;
    watchlistDataId?: string;
    addToWatchlistText: string;
    removeFromWatchlistText: string;
    r: any;
    index: number;
    isMobile: boolean;
    onChangeSelectionLists: (data: Record<string, string[]>) => any;
    selectionLists: Record<string, string[]>;
    filterBySelection: SelectionListData[];
    onClickCompare: (storageName: string, id: string, colId: string, changeToTab?: string) => any;
    onTriggerTracking: (
        event: string,
        currentTab?: string,
        fundName?: string,
        filterAnalyticLabel?: string,
        customValue?: string
    ) => any;
    staticDataTabIndex: number;
    useAnalytics: boolean;
    selectedTab?: string;
}
const CHILDREN_PLACEHOLDER_ID = 'children';

export default function TableRow(props: PropsShape) {
    const {
        columns,
        data,
        r,
        widgetInstance,
        setWatchlist,
        watchlist,
        watchlistDataId,
        index,
        onTriggerTracking,
        useAnalytics,
        ...rest
    } = props;

    const { Placeholder } = commonHooks.useAppContext();
    const i18n = commonHooks.useLocalization();
    const tableRef = useRef<HTMLDivElement | HTMLTableRowElement>(null);
    const WATCHLIST = rest.watchlistStorageName || 'watchlist';

    const setWatchlists = (data: any) => {
        const _isLocalStorageAccessible: boolean = isLocalStorageAccessible();

        if (_isLocalStorageAccessible) {
            const items: any = JSON.parse(localStorage.getItem(WATCHLIST) || '[]');
            const item = data[watchlistDataId || 'id'];

            if (!items?.includes(item)) {
                // handles case for adding to watchlist
                let temp = [item];
                items && temp.push(...items);
                temp = _.uniq(temp);
                localStorage.setItem(WATCHLIST, JSON.stringify(temp));
                onTriggerTracking('Add Watchlist', '', item);
            } else {
                // handle case for removing saved items
                items && _.remove(items, (d) => d === item);
                onTriggerTracking('Remove Watchlist', '', item);
                if (items.length < 1) {
                    // handle case for single saved item
                    localStorage.removeItem(WATCHLIST);
                } else {
                    // handle case for when memory holds other saved items
                    localStorage.setItem(WATCHLIST, JSON.stringify(items));
                }
            }

            const watchlistItems = localStorage.getItem(WATCHLIST);

            if (watchlistItems && watchlistItems.length) {
                setWatchlist(JSON.parse(watchlistItems || ''));
            } else {
                setWatchlist([]);
            }
        }
    };

    const setSelectionLists = (
        data: any,
        dataId: string,
        storageName: string,
        maxCount?: number,
        type?: 'compare' | 'downloadBooklet'
    ) => {
        const _isLocalStorageAccessible: boolean = isLocalStorageAccessible();

        if (type === 'downloadBooklet') {
            // Handle booklet type: store items in an array and pass to rest.onChangeSelectionLists
            const items = rest.selectionLists[storageName] || [];
            const item = data[dataId || 'id'];

            if (!items.includes(item)) {
                // Add item to the array if not already present
                if (typeof maxCount !== 'number' || items.length < maxCount) {
                    const updatedItems = [...items, item];
                    rest.onChangeSelectionLists({
                        ...rest.selectionLists,
                        [storageName]: updatedItems,
                    });
                }
            } else {
                // Remove item from the array if already present
                const updatedItems = items.filter((d) => d !== item);
                rest.onChangeSelectionLists({
                    ...rest.selectionLists,
                    [storageName]: updatedItems,
                });
            }
        } else if (_isLocalStorageAccessible) {
            const items: any = JSON.parse(localStorage.getItem(storageName) || '[]');
            const item = data[dataId || 'id'];
            if (!items?.includes(item)) {
                // handles case for adding to watchlist
                if (typeof maxCount !== 'number' || items.length < maxCount) {
                    let temp = [item];
                    items && temp.push(...items);
                    temp = _.uniq(temp);
                    localStorage.setItem(storageName, JSON.stringify(temp));
                }
            } else {
                // handle case for removing saved items
                items && _.remove(items, (d) => d === item);

                if (items.length < 1) {
                    // handle case for single saved item
                    localStorage.removeItem(storageName);
                } else {
                    // handle case for when memory holds other saved items
                    localStorage.setItem(storageName, JSON.stringify(items));
                }
            }

            const listItems: string[] = JSON.parse(localStorage.getItem(storageName) || '[]');

            if (listItems) {
                rest.onChangeSelectionLists({ ...rest.selectionLists, [storageName]: listItems });
            } else {
                const tmp: Record<string, string[]> = rest.selectionLists;
                delete tmp[storageName];
                rest.onChangeSelectionLists(tmp);
            }
        }
    };

    useEffect(() => {
        // tracks clicks on the table row
        const handleClickInside = (event: MouseEvent) => {
            if (tableRef.current && tableRef.current.contains(event.target as Node)) {
                const clickedElement = event.target as HTMLElement;
                // Extract custom values from the clicked <a> element
                const customValue = clickedElement.getAttribute('data-analytic-value') || '';
                if (customValue) {
                    onTriggerTracking('Table Option Click', '', '', '', customValue);
                }
            }
        };

        if (useAnalytics) {
            document.addEventListener('click', handleClickInside);
        }

        return () => {
            document.removeEventListener('click', handleClickInside);
        };
    }, [onTriggerTracking, useAnalytics]);

    const showWatchList = (column: ColumnOption): boolean => {
        let show: boolean = true;
        let fundIds: string[] = [];
        if (column?.excludeWatchListOpt === 'Individual Fund Ids') {
            fundIds = column?.excludeWatchListFundIds?.split(',') || [];
            show = !fundIds
                ?.map((id: any) => id?.toString())
                ?.includes(r[watchlistDataId || 'id']?.toString());
        } else if (column?.excludeWatchListOpt === 'Data key for Fund Ids') {
            fundIds = JsonQuery(column?.excludeWatchListFundIdsDataKey || '', { data }).value || [];
            show = !fundIds
                ?.map((id: any) => id?.toString())
                ?.includes(r[watchlistDataId || 'id']?.toString());
        } else if (column?.excludeWatchListOpt === 'Data key for condition') {
            show = !(i18n.format(column?.excludeWatchListConditionDataKey, {
                ...r,
                ...data,
            }) as any);
        }

        return show;
    };

    const showSelection = (column: ColumnOption): boolean => {
        let show: boolean = true;
        let fundIds: string[] = [];
        if (column?.excludeCompareOpt === 'Individual Fund Ids') {
            fundIds = column?.excludeCompareFundIds?.split(',') || [];
            show = !fundIds
                ?.map((id: any) => id?.toString())
                ?.includes(r[column.selectionListId || 'id']?.toString());
        } else if (column?.excludeCompareOpt === 'Data key for Fund Ids') {
            fundIds = JsonQuery(column?.excludeCompareFundIdsDataKey || '', { data }).value || [];
            show = !fundIds
                ?.map((id: any) => id?.toString())
                ?.includes(r[column.selectionListId || 'id']?.toString());
        } else if (column?.excludeCompareOpt === 'Data key for condition') {
            show = !(i18n.format(column?.excludeCompareConditionDataKey, {
                ...r,
                ...data,
            }) as any);
        }

        return show;
    };

    return (
        <>
            {rest.isMobile ? (
                <tr
                    className={'table-row'}
                    key={index}
                    ref={tableRef as React.RefObject<HTMLTableRowElement>}
                >
                    {columns?.map((c: any, i: number) => {
                        switch (c.type) {
                            case 'Placeholder':
                                return (
                                    <td key={`col-${i}`}>
                                        <Placeholder
                                            // eslint-disable-next-line max-len
                                            contentPlaceHolderId={`${CHILDREN_PLACEHOLDER_ID}-${c?.placeholderId}`}
                                            parentWidgetInstanceId={widgetInstance?.id}
                                            data={{ ...r, ...data }}
                                        />
                                    </td>
                                );
                            case 'Watchlist':
                                return (
                                    <td key={`col-${i}`}>
                                        {showWatchList(c) ? (
                                            c?.watchlistType === 'icon' && rest.enableWatchlist ? (
                                                <button
                                                    className={'watchlist-icon'}
                                                    onClick={() => setWatchlists(r)}
                                                    tabIndex={0}
                                                    aria-label={
                                                        watchlist.includes(
                                                            r[watchlistDataId || 'id']
                                                        )
                                                            ? rest.removeFromWatchlistText
                                                            : rest.addToWatchlistText
                                                    }
                                                >
                                                    <i
                                                        className={`icon ${
                                                            watchlist.includes(
                                                                r[watchlistDataId || 'id']
                                                            )
                                                                ? // eslint-disable-next-line max-len
                                                                  `favorite ${c?.selectedWatchlistIconName}`
                                                                : `${c?.watchlistIconName}`
                                                        }`}
                                                        title={
                                                            watchlist.includes(
                                                                r[watchlistDataId || 'id']
                                                            )
                                                                ? rest.removeFromWatchlistText
                                                                : rest.addToWatchlistText
                                                        }
                                                    />
                                                    {''}
                                                </button>
                                            ) : rest.enableWatchlist ? (
                                                <input
                                                    className={'selected-checkbox'}
                                                    data-testid={'selected-checkbox'}
                                                    type="checkbox"
                                                    aria-label={
                                                        watchlist.includes(
                                                            r[watchlistDataId || 'id']
                                                        )
                                                            ? rest.removeFromWatchlistText
                                                            : rest.addToWatchlistText
                                                    }
                                                    checked={watchlist.includes(
                                                        r[watchlistDataId || 'id']
                                                    )}
                                                    onChange={() => setWatchlists(r)}
                                                />
                                            ) : (
                                                ''
                                            )
                                        ) : (
                                            ''
                                        )}
                                        <span
                                            dangerouslySetInnerHTML={{
                                                __html: i18n.format(c.value, {
                                                    ...r,
                                                    ...data,
                                                }),
                                            }}
                                            tabIndex={rest.staticDataTabIndex}
                                        ></span>
                                    </td>
                                );
                            case 'Selection':
                                return (
                                    <td key={`col-${i}`}>
                                        {showSelection(c) ? (
                                            <input
                                                className={'select-checkbox'}
                                                data-testid={'select-checkbox'}
                                                type="checkbox"
                                                aria-label={
                                                    rest.selectionLists[
                                                        c.selectionListStorageName
                                                    ]?.includes(r[c.selectionListId || 'id'])
                                                        ? i18n.format(
                                                              c?.removeFromSelectionlistText || '',
                                                              {
                                                                  ...r,
                                                                  ...data,
                                                              }
                                                          ) || i18n.t('remove_from_selectionlist')
                                                        : i18n.format(
                                                              c?.addToSelectionlistText || '',
                                                              {
                                                                  ...r,
                                                                  ...data,
                                                              }
                                                          ) || i18n.t('add_to_selectionlist')
                                                }
                                                checked={
                                                    rest.selectionLists &&
                                                    rest.selectionLists[c.selectionListStorageName]
                                                        ?.length
                                                        ? rest.selectionLists[
                                                              c.selectionListStorageName
                                                          ]?.includes(r[c.selectionListId || 'id'])
                                                        : false
                                                }
                                                onChange={() => {
                                                    setSelectionLists(
                                                        r,
                                                        c.selectionListId,
                                                        c.selectionListStorageName,
                                                        c.maxSelectionCount,
                                                        c.selectionUsedFor
                                                    );
                                                    c.selectionUsedFor === 'compare' &&
                                                        onTriggerTracking(
                                                            'Compare',
                                                            '',
                                                            r.fundName || ''
                                                        );
                                                }}
                                            />
                                        ) : (
                                            ''
                                        )}
                                    </td>
                                );
                            default:
                                return (
                                    <td
                                        key={`col-${i}`}
                                        tabIndex={rest.staticDataTabIndex}
                                        dangerouslySetInnerHTML={{
                                            __html: i18n.format(c.value, {
                                                ...r,
                                                ...data,
                                            }),
                                        }}
                                    />
                                );
                        }
                    })}
                </tr>
            ) : (
                <div className={`card ${rest.selectedTab}`} key={index} ref={tableRef}>
                    {columns?.map((c: any, i: number) => {
                        return (
                            <>
                                {showSelection(c) && c.type === 'Selection' ? (
                                    <div className="card-row" key={`col-${i}`}>
                                        <div className="left">
                                            <button
                                                disabled={
                                                    !rest.selectionLists[c.selectionListStorageName]
                                                        ?.length
                                                }
                                                className={
                                                    rest.filterBySelection?.filter(
                                                        (d) => d.colId === c.id
                                                    )?.length
                                                        ? 'selected'
                                                        : ''
                                                }
                                                tabIndex={rest.staticDataTabIndex}
                                                onClick={() => {
                                                    rest.selectionLists[c.selectionListStorageName]
                                                        ?.length &&
                                                        rest.onClickCompare(
                                                            c.selectionListStorageName,
                                                            c.selectionListId,
                                                            c.id,
                                                            c.changeToTab
                                                        );
                                                }}
                                            >
                                                {i18n.format(c.label, data)}
                                            </button>
                                        </div>
                                        <div className="right " key={`col-${i}`}>
                                            <input
                                                className={'select-checkbox'}
                                                data-testid={'select-checkbox'}
                                                type="checkbox"
                                                tabIndex={0}
                                                aria-label={
                                                    rest.selectionLists[
                                                        c.selectionListStorageName
                                                    ]?.includes(r[c.selectionListId || 'id'])
                                                        ? i18n.format(
                                                              c?.removeFromSelectionlistText || '',
                                                              {
                                                                  ...r,
                                                                  ...data,
                                                              }
                                                          ) || i18n.t('remove_from_selectionlist')
                                                        : i18n.format(
                                                              c?.addToSelectionlistText || '',
                                                              {
                                                                  ...r,
                                                                  ...data,
                                                              }
                                                          ) || i18n.t('add_to_selectionlist')
                                                }
                                                checked={
                                                    rest.selectionLists &&
                                                    rest.selectionLists[c.selectionListStorageName]
                                                        ?.length
                                                        ? rest.selectionLists[
                                                              c.selectionListStorageName
                                                          ]?.includes(r[c.selectionListId || 'id'])
                                                        : false
                                                }
                                                onChange={() => {
                                                    setSelectionLists(
                                                        r,
                                                        c.selectionListId,
                                                        c.selectionListStorageName,
                                                        c.maxSelectionCount
                                                    );
                                                    onTriggerTracking(
                                                        'Compare',
                                                        '',
                                                        r.fundName || ''
                                                    );
                                                }}
                                            />
                                        </div>
                                    </div>
                                ) : c.type !== 'Selection' ? (
                                    <div className="card-row" key={`col-${i}`}>
                                        <div
                                            className="left"
                                            dangerouslySetInnerHTML={{
                                                __html: i18n.format(c.label, data),
                                            }}
                                        />
                                        <>
                                            {(() => {
                                                switch (c.type) {
                                                    case 'Placeholder':
                                                        return (
                                                            <div
                                                                className="right "
                                                                key={`col-${i}`}
                                                            >
                                                                <Placeholder
                                                                    // eslint-disable-next-line max-len
                                                                    contentPlaceHolderId={`${CHILDREN_PLACEHOLDER_ID}-${c?.placeholderId}`}
                                                                    parentWidgetInstanceId={
                                                                        widgetInstance?.id
                                                                    }
                                                                    data={{
                                                                        ...r,
                                                                        ...data,
                                                                    }}
                                                                />
                                                            </div>
                                                        );
                                                    case 'Watchlist':
                                                        return (
                                                            <div
                                                                className="right "
                                                                key={`col-${i}`}
                                                            >
                                                                <span
                                                                    tabIndex={
                                                                        rest.staticDataTabIndex
                                                                    }
                                                                    dangerouslySetInnerHTML={{
                                                                        __html: i18n.format(
                                                                            c.value,
                                                                            {
                                                                                ...r,
                                                                                ...data,
                                                                            }
                                                                        ),
                                                                    }}
                                                                ></span>
                                                            </div>
                                                        );
                                                    default:
                                                        return (
                                                            <span
                                                                className="right "
                                                                key={`col-${i}`}
                                                                tabIndex={rest.staticDataTabIndex}
                                                                dangerouslySetInnerHTML={{
                                                                    __html: i18n.format(c.value, {
                                                                        ...r,
                                                                        ...data,
                                                                    }),
                                                                }}
                                                            />
                                                        );
                                                }
                                            })()}
                                        </>
                                    </div>
                                ) : null}
                            </>
                        );
                    })}

                    {columns?.map((c: any, i: number) =>
                        c.type === 'Watchlist' && showWatchList(c) ? (
                            <div className="card-row" key={`col-${i}`}>
                                <div className="left">{i18n.t('Add_to_watchlist')}</div>
                                <div className="right">
                                    {c?.watchlistType === 'icon' && rest.enableWatchlist ? (
                                        <button
                                            className={'watchlist-icon'}
                                            onClick={() => setWatchlists(r)}
                                            tabIndex={0}
                                        >
                                            <i
                                                className={`icon ${
                                                    watchlist.includes(r[watchlistDataId || 'id'])
                                                        ? `favorite ${c?.selectedWatchlistIconName}`
                                                        : `${c?.watchlistIconName}`
                                                }`}
                                                title={
                                                    watchlist.includes(r[watchlistDataId || 'id'])
                                                        ? rest.removeFromWatchlistText
                                                        : rest.addToWatchlistText
                                                }
                                            />
                                        </button>
                                    ) : rest.enableWatchlist ? (
                                        <input
                                            className={'selected-checkbox'}
                                            data-testid={'selected-checkbox'}
                                            type="checkbox"
                                            aria-label={
                                                watchlist.includes(r[watchlistDataId || 'id'])
                                                    ? rest.removeFromWatchlistText
                                                    : rest.addToWatchlistText
                                            }
                                            checked={watchlist.includes(r[watchlistDataId || 'id'])}
                                            onChange={() => setWatchlists(r)}
                                        />
                                    ) : (
                                        ''
                                    )}
                                </div>
                            </div>
                        ) : (
                            ''
                        )
                    )}
                </div>
            )}
        </>
    );
}
